import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VStack from "@components/layout/VStack";
import TitlePage from "@components/layout/TitlePage";
import { defaultPagination, titlePages } from "@shared/constants/appConstants";
import FindAvailableTime from "@features/search/components/FindAvailableTime";
import {
  useAppointmentStore,
  useOutlookStore,
  useResourceStore,
  useSettingStore,
  useThemeStore,
  useFindColleagueStore,
  useAttendeesStore,
  useAppConfigStore,
} from "@shared/stores";
import { useDebounce } from "@/pages/taskpane/shared/hooks/useDebounce";
import useScrollShadow from "@shared/hooks/useScrollShadow";
import DivShadow from "@components/layout/DivShadow";
import HStack from "@components/layout/HStack";
import Button from "@components/ui/Button";
import { Loader } from "@components/ui/Loader";
import ResourceCard from "@features/resources/components/ResourceCard";
import { DeskLocationEntity, ResourceEntity } from "@features/resources/services/types/resourceEntity";
import Input from "@components/ui/Input";
import {
  ArrowRightIcon,
  ChevronLeftBlueIcon,
  ClockIcon,
  LocationIcon,
  MagnifyingGlass,
  MapSearchIcon,
  PersonIcon,
  ResourceIcon,
  TextDescriptionIcon,
} from "@/pages/taskpane/components/icons/IconCustom";
import { useTranslation } from "react-i18next";
import { useFindRecentColleague } from "../services/useFindRecentColleague";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import { useFormatDateTime, useScrollLoadMore } from "@/pages/taskpane/shared/hooks";
import { useGetOrganizerAssistant } from "../../settings/services/useGetOrganizerAssistant";
import { OrganizerEntity } from "../../settings/services/entities/organizerEntity";
import { useGetMeetingColleagues } from "../services/useGetMeetingColleagues";
import { MeetingEntity } from "../services/entities/meetingEntity";
import { useFilterResources } from "../../search/services/useFilterResources";
import {
  CategoryOperator,
  ComparisonOperator,
  SortBy,
  SortByValue,
} from "@/pages/taskpane/shared/types/enums/globalEnum";
import { convertParsedTimeToString, parseISOString, isSameDay } from "@/pages/taskpane/features/resources/ulti";
import { useNavigate } from "react-router-dom";
import EmptyData from "@/pages/taskpane/components/ui/EmptyData";
import { routes } from "@/pages/taskpane/shared/constants/routes";
import { IExpandDialogData } from "@/pages/taskpane/shared/types/expandDialog";
import { useMainTemplate } from "@/pages/taskpane/components/hooks/useMainTemplate";
import Tooltip from "@/pages/taskpane/components/ui/Tooltip";
import { useAuthStore } from "../../auth/store/authStore";

type ColleagueType = {
  name: string;
  email: string;
};

const FindColleague = () => {
  const { t } = useTranslation();
  const { isDarkMode, themeId } = useThemeStore();
  const navigate = useNavigate();
  const { handleFormatDateWithSeparator, handleFormatTimeWithLanguage } = useFormatDateTime();
  const {
    organizer,
    customUid,
    globalId,
    allAttendees,
    recurringDates,
    isEditingRecurringSeries,
    isReopenAppointment,
    startDateTimeZone,
    endDateTimeZone,
    iCalUId,
    startTime,
    endTime,
    isNoEndDateRecurring,
    recurrence,
  } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { selectedResource, filterResource, deskLocationList, meetingLabel: meetingLabelResource } = useResourceStore();
  const {
    recommendationScreen,
    tileConfiguration,
    defaultResourceType,
    defaultLocations,
    dateFormat,
    timeFormat,
    firstDayOfWeek,
    dateSeparator,
  } = useSettingStore();
  const {
    meetingSelected,
    meetings,
    colleagueSelected,
    textSearch,
    setMeetingSelected,
    setMeetings,
    setColleagueSelected,
    setTextSearch,
  } = useFindColleagueStore();
  const { requiredAttendees, optionalAttendees, mainAttendee, locationUnknown } = useAttendeesStore();

  const {
    language,
    locations,
    categories,
    resourceTypes,
    helpUrlForOutLookAddin,
    parameters,
    meetingLabel,
    defaultMeetingLabel,
  } = useAppConfigStore();

  const { handleOpenDialogExpand } = useMainTemplate();
  const { auth } = useAuthStore();

  const listMeetingRef = useRef<HTMLDivElement | null>(null);
  const resourceListRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { setDeskLocationList } = useResourceStore();
  const [isFocusInput, setIsFocusInput] = useState(false);
  const textDebounced = useDebounce(textSearch, 500);

  // Get Recent Colleagues

  const findRecentColleaguePayload = useMemo(() => {
    return {
      requestId: generateCId(),
      organizer: organizer,
      startDate: startDateTimeUtc,
      endDate: endDateTimeUtc,
      timeZoneStandardName: timeZone,
      pageSize: defaultPagination.pageSizeFindRecentColleague,
    };
  }, [organizer, startDateTimeUtc, endDateTimeUtc, timeZone]);

  const {
    data: recentColleagueResult,
    isError,
    isFetching: isFetchingRecentColleague,
    fetchNextPage,
  } = useFindRecentColleague({
    payload: findRecentColleaguePayload,
    enabled: isFocusInput && !textDebounced?.trim(),
  });

  const recentColleague = useMemo(() => {
    const allResources = recentColleagueResult?.pages?.flatMap((page) => page.recents) || [];
    return allResources;
  }, [recentColleagueResult]);

  // Search Colleagues

  const findOrganizerAssistantPayload = useMemo(() => {
    return {
      organizerEmail: organizer,
      searchText: textDebounced || "",
    };
  }, [organizer, textDebounced]);

  const { data: colleagueDataResult, isFetching: isFetchingSearchColleague } = useGetOrganizerAssistant({
    payload: findOrganizerAssistantPayload,
    enabled: !!textDebounced.trim() && isFocusInput,
  });

  const colleagueOptions = useMemo(() => {
    return (colleagueDataResult || [])?.map((item: OrganizerEntity) => ({
      name: item.defaultAssistantName,
      email: item.defaultAssistantEmail,
    }));
  }, [colleagueDataResult]);

  // Colleague Data hiện ở dropdown

  const colleagueData = useMemo(() => {
    return textDebounced ? colleagueOptions : recentColleague;
  }, [textDebounced, colleagueOptions, recentColleague]);

  // Get Meetings

  const findMeetingColleaguesPayload = useMemo(() => {
    let startOfDay = "";
    let endOfDay = "";
    if (startDateTimeUtc) {
      const currentDate = new Date();
      const currentDateISO = currentDate.toISOString().split('T')[0];

      const startDate = new Date(startDateTimeUtc);
      const startYear = startDate.getFullYear();
      const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
      const startDay = String(startDate.getDate()).padStart(2, "0");
      const startHours = String(startDate.getHours()).padStart(2, "0");
      const startMinutes = String(startDate.getMinutes()).padStart(2, "0");
      const startSeconds = String(startDate.getSeconds()).padStart(2, "0");
      const appointmentDateISO = `${startYear}-${startMonth}-${startDay}`;

      const isSameDayCurrent = isSameDay(parseISOString(currentDateISO), parseISOString(appointmentDateISO));

      const endDate = new Date(endDateTimeUtc);
      const endYear = endDate.getFullYear();
      const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
      const endDay = String(endDate.getDate()).padStart(2, "0");

      if (isSameDayCurrent) {
        startOfDay = `${startYear}-${startMonth}-${startDay}T${startHours}:${startMinutes}:${startSeconds}.000Z`;
      } else {
        startOfDay = `${startYear}-${startMonth}-${startDay}T00:00:00.000Z`;
      }
      endOfDay = `${endYear}-${endMonth}-${endDay}T23:59:59.999Z`;
    }

    return {
      email: colleagueSelected?.email || "",
      name: colleagueSelected?.name || "",
      requestId: generateCId(),
      organizer: organizer || "",
      startDate: startOfDay,
      endDate: endOfDay,
      timeZoneStandardName: timeZone,
      pageSize: defaultPagination.pageSizeFindMeetingColleagues || 100,
    };
  }, [colleagueSelected?.email, colleagueSelected?.name, organizer, startDateTimeUtc,endDateTimeUtc, timeZone]);

  const {
    data: meetingColleaguesResult,
    isFetching: isFetchingMeetingColleagues,
    fetchNextPage: fetchNextPageMeetingColleagues,
    isError: isErrorMeetingColleagues,
  } = useGetMeetingColleagues({
    payload: findMeetingColleaguesPayload,
    enabled: !!colleagueSelected?.email.trim() && !!colleagueSelected?.name.trim(),
  });

  const meetingsData = useMemo(() => {
    return meetingColleaguesResult?.pages?.flatMap((page) => page.colleagues) || [];
  }, [meetingColleaguesResult]);

  useEffect(() => {
    setMeetings(meetingsData);
    setMeetingSelected(null);
  }, [meetingsData, setMeetings]);

  const currentMeetingIndex = useMemo((): number => {
    if (!meetings?.length || !meetingSelected) return 0;
    return meetings.findIndex((meeting) => meeting.id === meetingSelected.id);
  }, [meetings, meetingSelected]);

  const handleSetSelectedColleague = useCallback((colleague: ColleagueType) => {
    setColleagueSelected(colleague);
    setIsFocusInput(false);
    setTextSearch(colleague.email);
  }, []);

  //  Next or Previous Meeting

  const handleSwitchMeeting = useCallback(
    (index: number) => {
      if (index < 0 || index >= meetings.length) return;
      setMeetingSelected(meetings[index]);
    },
    [meetings]
  );

  //  Find Resource Nearby

  const handleFindResourceNearby = useCallback((meeting: MeetingEntity) => {
    setMeetingSelected(meeting);
  }, []);

  const getResourceNearbyPayload = useMemo(
    () => ({
      pageSize: defaultPagination?.pageSize || 10,
      requestId: generateCId(),
      uid: customUid || "",
      globalId: globalId || "",
      organizer: organizer || "",
      resourceType: "",
      locationIds: meetingSelected?.resource?.locationIds || [],
      timeZoneStandardName: timeZone || "",
      attendees: [organizer, ...allAttendees.map((att) => att.Email)],
      occurrences: [
        {
          utcStartDate: startDateTimeUtc || "",
          utcEndDate: endDateTimeUtc || "",
        },
      ],
      isNoEndDate: null,
      isRecurring: false,
      isOnlyAvailable: false,
      sortBy: {
        field: SortByValue.name,
        operator: SortByValue.operatorAsc,
      },
    }),
    [
      organizer,
      startDateTimeUtc,
      endDateTimeUtc,
      timeZone,
      allAttendees,
      customUid,
      globalId,
      meetingSelected?.resource?.locationIds,
    ]
  );

  const {
    data: resourceNearbyDataResult,
    fetchNextPage: fetchNextPageResourceNearby,
    isFetching: isFetchingResourceNearby,
    isError: isErrorResourceNearby,
  } = useFilterResources({
    payload: getResourceNearbyPayload,
    enabled: Boolean(meetingSelected),
  });

  // Extract desk locations separately
  const extractedDeskLocations = useMemo(() => {
    const allDeskLocations = resourceNearbyDataResult?.pages?.flatMap((page) => page.deskLocations) || [];
    const deskLocationList: DeskLocationEntity[] = [];
    allDeskLocations.forEach((location) => {
      if (!deskLocationList.some((item) => item.locationId === location.locationId)) {
        deskLocationList.push(location);
      }
    });
    return deskLocationList;
  }, [resourceNearbyDataResult]);

  // Update store when desk locations change
  useEffect(() => {
    setDeskLocationList(extractedDeskLocations);
  }, [extractedDeskLocations, setDeskLocationList]);

  const resourceList = useMemo(() => {
    const allResources = resourceNearbyDataResult?.pages?.flatMap((page) => page.resources) || [];
    return allResources.filter((r: ResourceEntity) => !selectedResource.some((c) => c.id === r.id));
  }, [resourceNearbyDataResult, selectedResource]);

  const rangeTimeLabel = useCallback(
    (startDate: string, endDate: string) => {
      if (!startDate || !endDate) return "";

      const startParsed = parseISOString(startDate);
      const endParsed = parseISOString(endDate);
      const dateStr = handleFormatDateWithSeparator(startParsed);
      const startTime = handleFormatTimeWithLanguage(convertParsedTimeToString(startParsed));
      const endTime = handleFormatTimeWithLanguage(convertParsedTimeToString(endParsed));

      return `${dateStr}, ${startTime} - ${endTime}`;
    },
    [handleFormatDateWithSeparator, handleFormatTimeWithLanguage]
  );

  // Load More Recent Colleague

  useScrollLoadMore({
    containerRef: dropdownRef,
    hasMore: !isError && !recentColleagueResult?.pages[recentColleagueResult?.pages?.length - 1]?.isLastPage,
    isLoading: isFetchingRecentColleague,
    onLoadMore: () => {
      fetchNextPage();
    },
    threshold: 200,
  });

  // Load More Meeting

  useScrollLoadMore({
    containerRef: listMeetingRef,
    hasMore:
      !isErrorMeetingColleagues &&
      !meetingColleaguesResult?.pages[meetingColleaguesResult?.pages?.length - 1]?.isLastPage,
    isLoading: isFetchingMeetingColleagues,
    onLoadMore: () => {
      fetchNextPageMeetingColleagues();
    },
    threshold: 200,
  });

  // Load More Resource Nearby

  useScrollLoadMore({
    containerRef: resourceListRef,
    hasMore:
      Boolean(meetingSelected) &&
      !isErrorResourceNearby &&
      !resourceNearbyDataResult?.pages[resourceNearbyDataResult?.pages?.length - 1]?.isLastPage,
    isLoading: isFetchingResourceNearby,
    onLoadMore: () => {
      if (meetingSelected) {
        fetchNextPageResourceNearby();
      }
    },
    threshold: 200,
  });

  useScrollShadow(listMeetingRef);
  useScrollShadow(resourceListRef);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFocusInput(false);
      }
    };
    if (isFocusInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocusInput]);

  // No support recurring
  useEffect(() => {
    if ((!isReopenAppointment && recurringDates?.length > 0) || (isReopenAppointment && isEditingRecurringSeries)) {
      navigate(recommendationScreen ? routes.recommendation : routes.search);
    }
  }, [recurringDates, isEditingRecurringSeries, isReopenAppointment]);

  const handleShowFloorplan = (meeting: MeetingEntity) => {
    const currentResourceType = defaultLocations.defaultResourceTypes.find((i) => i.type === defaultResourceType);
    const locationFilter = currentResourceType?.locationIds?.split(",").map(Number) || [];
    const meetingsData = JSON.parse(JSON.stringify(meetings));
    const meetingSelectedData = JSON.parse(JSON.stringify(meeting));
    const data: IExpandDialogData = {
      tokens: auth?.tokens || null,
      selectedResources: selectedResource.map((resource) => resource.toJson()),
      requiredAttendees: requiredAttendees.map((attendee) => attendee.toJson()),
      optionalAttendees: optionalAttendees.map((attendee) => attendee.toJson()),
      mainAttendee: mainAttendee?.toJson() || null,
      locationUnknown: locationUnknown,
      recurringDates: recurringDates,
      recurrence: recurrence,
      timeZone: timeZone || "",
      startDateTimeZone: startDateTimeZone,
      endDateTimeZone: endDateTimeZone,
      startDateTimeUtc: startDateTimeUtc,
      endDateTimeUtc: endDateTimeUtc,
      startTime: startTime?.toISOString() || "",
      endTime: endTime?.toISOString() || "",
      iCalUId: iCalUId || "",
      globalId: globalId || "",
      customUid: customUid || "",
      isDarkMode: isDarkMode,
      themeId: themeId,
      firstDayOfWeek: firstDayOfWeek,
      dateSeparator: dateSeparator,
      timeFormat: timeFormat,
      dateFormat: dateFormat,
      language: language,
      tileConfiguration: JSON.parse(JSON.stringify(tileConfiguration)),
      defaultResourceType,
      defaultLocations: JSON.parse(JSON.stringify(defaultLocations)),
      locations: JSON.parse(JSON.stringify(locations)),
      categories: JSON.parse(JSON.stringify(categories)),
      resourceTypes: JSON.parse(JSON.stringify(resourceTypes)),
      helpUrlForOutLookAddin: helpUrlForOutLookAddin,
      parameters: JSON.parse(JSON.stringify(parameters)),
      meetingLabel: JSON.parse(JSON.stringify(meetingLabel)) || null,
      defaultMeetingLabel: JSON.parse(JSON.stringify(defaultMeetingLabel)) || null,
      deskLocationList: JSON.parse(JSON.stringify(deskLocationList)),
      meetingLabelResource: meetingLabelResource || null,
      filterResource: {
        resourceType: defaultResourceType,
        locations: filterResource?.locations?.length ? filterResource.locations : locationFilter,
        textSearch: filterResource?.textSearch ?? "",
        status: filterResource?.status ?? true,
        capacityOperator: filterResource?.capacityOperator ?? ComparisonOperator.MoreThan,
        capacityValue: filterResource?.capacityValue ?? 0,
        sortBy: filterResource?.sortBy ?? SortBy.NameAsc,
        categories: filterResource?.categories?.length ? filterResource.categories : [],
        categoryOperator: filterResource?.categoryOperator || CategoryOperator.Or,
      },
      meetings: meetingsData,
      meetingSelected: meetingSelectedData,
      isNoEndDateRecurring: isNoEndDateRecurring || null,
      capacityFilter: null,
      colleagueSelected: colleagueSelected,
    };
    let origin = window.location.origin;
    if (!origin.includes("localhost") && !origin.includes("client-app-2.pages.dev")) {
      origin = `${origin}/ClientApps`;
    }
    const dialogUrl = `${origin}/expand.html?dialog=true`;
    handleOpenDialogExpand(dialogUrl, data);
  };

  // useEffect(() => {
  //   clearFindColleague();
  // }, []);

  return (
    <VStack className="h-full px-4 pt-1 pb-4" gap={3}>
      {/* Title */}
      <TitlePage title={t(titlePages.findColleague.title)} />

      <VStack className="w-full flex-1 overflow-hidden" gap={3}>
        {/* Search Input */}
        {!meetingSelected ? (
          <div className="w-full relative" ref={dropdownRef}>
            <Input
              leftIcon={<MagnifyingGlass isDarkMode={isDarkMode} isOffHover />}
              placeholder={t("Enter colleague name")}
              value={textSearch}
              onChange={(e) => setTextSearch(e.target.value)}
              className="h-[32px] text-[14px]"
              isHighlightBorder={isFocusInput}
              onFocus={() => setIsFocusInput(true)}
            />

            {isFocusInput ? (
              <div className="w-full px-[2px] absolute z-10">
                <DivShadow className="rounded bg-white dark:bg-black dark:border-none p-1">
                  {colleagueData.length ? (
                    <>
                      {!textDebounced ? (
                        <div className="px-2 py-[6px] text-sm font-semibold">{t("Recent work items")}</div>
                      ) : null}

                      <VStack className="w-full max-h-[200px] overflow-y-auto no-scrollbar">
                        {colleagueData.map((item: ColleagueType, index: number) => (
                          <HStack
                            key={index}
                            className={`w-full px-[10px] py-2 text-xs cursor-pointer hover:bg-extraLightGray dark:hover:bg-deepGray dark:text-white ${colleagueSelected?.email === item.email
                              ? "bg-softBlue hover:bg-softBlue dark:bg-midnightBlue dark:hover:bg-midnightBlue"
                              : ""
                              }`}
                            gap={2}
                            onClick={() => handleSetSelectedColleague(item)}
                          >
                            <HStack justifyContent="center" className="w-8 h-8 rounded-full bg-primaryBlue flex-shrink-0">
                              <PersonIcon isDarkMode={isDarkMode} color="#fff" />
                            </HStack>

                            <div className="flex-1 min-w-0">
                              <p className=" text-sm font-semibold truncate">{item.name}</p>
                              <p className="text-mediumGray text-xs truncate">{item.email}</p>
                            </div>
                          </HStack>
                        ))}
                      </VStack>
                    </>
                  ) : isFetchingSearchColleague || isFetchingRecentColleague ? (
                    <HStack className="w-full justify-center p-4">
                      <Loader texts={[t("Loading...")]} />
                    </HStack>
                  ) : (
                    <EmptyData content={t("No data available")} />
                  )}
                </DivShadow>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Colleague Selected */}

        {colleagueSelected ? (
          <HStack
            className="w-full border border-neutralGray rounded-[4px] p-2 bg-white dark:border-balancedGray dark:bg-darkBackgroundColor"
            gap={1}
          >
            {meetingSelected ? (
              <Button
                leftIcon={<ChevronLeftBlueIcon isDarkMode={isDarkMode} size={24} className="" />}
                onClick={() => {
                  setMeetingSelected(null);
                }}
              />
            ) : null}

            <HStack justifyContent="center" className="w-9 h-9 rounded-full bg-primaryBlue flex-shrink-0">
              <PersonIcon isDarkMode={isDarkMode} color="#fff" />
            </HStack>
            <div className="flex-1 min-w-0">
              <div className="flex gap-1">
                <p className="min-w-0 text-sm font-semibold truncate">{colleagueSelected.name}</p>
                {meetings.length ? <span className="flex-shrink-0 text-sm font-semibold">({meetings.length})</span> : null}
              </div>
              <p className="text-xs text-mediumGray dark:text-midGray truncate">{colleagueSelected.email}</p>
            </div>
          </HStack>
        ) : null}

        {/* Meeting Detail */}

        {meetingSelected ? (
          <div className="px-3 py-3 w-full bg-white border border-lightGray rounded-lg dark:bg-darkBackgroundColor dark:border-darkMediumGray">
            <div className="pb-2 mb-2 border-b-[1px] border-lightGray dark:border-darkMediumGray">
              <HStack className="w-full" justifyContent="between">
                <HStack gap={2}>
                  <HStack gap={1}>
                    <ArrowRightIcon
                      isDarkMode={isDarkMode}
                      isActive={currentMeetingIndex !== 0}
                      className={`rotate-180 ${currentMeetingIndex === 0 ? "cursor-default" : "cursor-pointer"}`}
                      onClick={() => {
                        if (currentMeetingIndex === 0) return;
                        handleSwitchMeeting(currentMeetingIndex - 1);
                      }}
                    />
                    <ArrowRightIcon
                      isDarkMode={isDarkMode}
                      isActive={currentMeetingIndex !== meetings.length - 1}
                      className={`${currentMeetingIndex === meetings.length - 1 ? "cursor-default" : "cursor-pointer"}`}
                      onClick={() => {
                        if (currentMeetingIndex === meetings.length - 1) return;
                        handleSwitchMeeting(currentMeetingIndex + 1);
                      }}
                    />
                  </HStack>
                  <p className="text-sm">{meetingSelected?.subject}</p>
                </HStack>
              </HStack>
            </div>

            <VStack className="w-full">
              <RowItem
                icon={<ClockIcon isDarkMode={isDarkMode} isActive={true} size={16} />}
                value={rangeTimeLabel(meetingSelected?.startDate || "", meetingSelected?.endDate || "")}
                className="mb-2"
                onShowFloorplan={() => handleShowFloorplan(meetingSelected)}
                meeting={meetingSelected}
              />
              <RowItem
                icon={<ResourceIcon isDarkMode={isDarkMode} isActive={true} size={16} isOffHover />}
                value={meetingSelected?.resource.name || ""}
                className="mb-2"
              />
              <RowItem
                icon={<LocationIcon isDarkMode={isDarkMode} isActive={true} size={16} />}
                value={meetingSelected?.resource.locations || ""}
              />
            </VStack>
          </div>
        ) : null}

        {/* meeting list */}

        <VStack
          ref={listMeetingRef}
          className={`flex-1 overflow-y-auto no-scrollbar ${!meetingSelected && colleagueSelected ? "" : "hidden"}`}
          gap={3}
        >
          {meetings.length ? (
            meetings.map((meeting, index) => (
              <Tooltip key={index} message={t("Find resource nearby")} className="w-full">
                <VStack
                  onClick={() => handleFindResourceNearby(meeting)}
                  className="w-full bg-white hover:bg-extraLightGray dark:hover:bg-deepGray dark:bg-darkBackgroundColor border border-lightGray rounded-lg p-3 dark:border-darkMediumGray cursor-pointer hover:border-primaryBlue dark:hover:border-brightBlue last:mb-[1px]"
                >
                  <RowItem
                    icon={<ClockIcon isDarkMode={isDarkMode} isActive={true} size={16} />}
                    value={rangeTimeLabel(meeting.startDate, meeting.endDate)}
                    className="mb-2"
                    meeting={meeting}
                    onShowFloorplan={() => handleShowFloorplan(meeting)}
                  />
                  <RowItem
                    icon={<TextDescriptionIcon isDarkMode={isDarkMode} isActive={true} size={16} />}
                    value={meeting.subject}
                    className="mb-2"
                  />
                  <RowItem
                    icon={<ResourceIcon isDarkMode={isDarkMode} isActive={true} size={16} isOffHover />}
                    value={meeting.resource.name}
                    className="mb-2"
                  />
                  <RowItem
                    icon={<LocationIcon isDarkMode={isDarkMode} isActive={true} size={16} />}
                    value={meeting.resource.locations}
                  />
                </VStack>
              </Tooltip>
            ))
          ) : !isFetchingMeetingColleagues ? (
            <EmptyData content={t("No data available")} />
          ) : null}

          {isFetchingMeetingColleagues ? (
            <HStack className="w-full justify-center p-4">
              <Loader texts={[t("Loading...")]} />
            </HStack>
          ) : null}
        </VStack>

        {/* List Resource Nearby */}
        <VStack className={`flex-1 overflow-hidden ${meetingSelected ? "" : "hidden"}`}>
          <p className="font-semibold mb-2">{t("List nearby resources")}</p>
          {resourceList?.length ? (
            <VStack ref={resourceListRef} className={`flex-1 overflow-y-auto no-scrollbar`} gap={2}>
              {resourceList.map((resource, index) => (
                <ResourceCard key={index} resource={resource} isSelected={false} />
              ))}
            </VStack>
          ) : !isFetchingResourceNearby ? (
            <EmptyData content={t("No Resources available")} />
          ) : null}

          {isFetchingResourceNearby ? (
            <HStack className="w-full justify-center p-4">
              <Loader texts={[t("Loading...")]} />
            </HStack>
          ) : null}
        </VStack>
      </VStack>

      {/* Find Available Time */}
      <FindAvailableTime />
    </VStack>
  );
};

export default FindColleague;

// CUstom Row Item Common

const RowItem = ({
  icon,
  value,
  className,
  meeting,
  onShowFloorplan,
}: {
  icon: React.ReactNode;
  value: string;
  className?: string;
  meeting?: MeetingEntity;
  onShowFloorplan?: () => void;
}) => {
  const { t } = useTranslation();

  const renderButton = () => {
    if (meeting?.resource?.hasFloorplan) {
      return (
        <Tooltip message={t("Floor Plan")}>
          <Button
            className="px-2 py-1 text-xs rounded"
            isDefault={true}
            onClick={(e) => {
              e.stopPropagation();
              onShowFloorplan?.();
            }}
            leftIcon={<MapSearchIcon color="#ffffff" />}
          />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <HStack
      className={`w-full ${className}`}
      justifyContent={meeting?.resource?.hasFloorplan ? "between" : "start"}
      alignItems={meeting?.resource?.hasFloorplan ? "end" : "center"}
    >
      <HStack className={`${meeting?.resource?.hasFloorplan ? "flex-1 truncate" : "w-full"}`} gap={2}>
        <div>{icon}</div>
        <p className={`text-xs flex-1 truncate`}>{value}</p>
      </HStack>

      {renderButton()}
    </HStack>
  );
};
